import { safeSetItem } from './storageUtils';

/**
 * Real-time User Presence & Session Tracking Service
 * Tracks active users across browser tabs, windows, and login sessions using
 * LocalStorage sync and BroadcastChannel.
 */

const STORAGE_KEY = 'egurukul_live_sessions';
const BROADCAST_CHANNEL_NAME = 'egurukul_presence_channel';
const ACTIVE_THRESHOLD_MS = 30 * 60 * 1000; // 30 mins active window for logged in sessions
const IDLE_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours idle window

let broadcastChannel = null;
try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }
} catch (e) {
  console.warn('[Presence] BroadcastChannel error:', e);
}

// Listen for broadcast messages from other tabs/windows
if (broadcastChannel) {
  broadcastChannel.onmessage = (event) => {
    try {
      if (event.data?.type === 'HEARTBEAT' && event.data?.session) {
        const session = event.data.session;
        const key = (session.email || session.id || '').toLowerCase();
        if (key) {
          const sessions = getStoredLiveSessions();
          sessions[key] = session;
          safeSetItem(STORAGE_KEY, JSON.stringify(sessions));
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('egurukul:presence_updated', { detail: session }));
          }
        }
      }
    } catch (e) {
      console.warn('[Presence] Broadcast message handling error:', e);
    }
  };
}

// Listen for storage events from other windows
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      window.dispatchEvent(new CustomEvent('egurukul:presence_updated'));
    }
  });
}

// Get all recorded live sessions from storage, optionally filtered by orgId
export const getStoredLiveSessions = (filterOrgId = null) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const valid = {};

    // Keep sessions that were active within the last 2 hours, sorted by most recent, capped at 30 entries
    const sortedEntries = Object.entries(parsed)
      .filter(([_, session]) => {
        if (!session || !session.lastActive) return false;
        if (now - session.lastActive >= IDLE_THRESHOLD_MS) return false;
        if (filterOrgId) {
          const sessionOrg = session.orgId ? String(session.orgId).trim().toLowerCase() : '';
          const targetOrg = String(filterOrgId).trim().toLowerCase();
          if (!sessionOrg || sessionOrg !== targetOrg) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => (b[1].lastActive || 0) - (a[1].lastActive || 0))
      .slice(0, 30);

    sortedEntries.forEach(([key, session]) => {
      valid[key] = session;
    });

    return valid;
  } catch (err) {
    return {};
  }
};

// Subscribe to presence updates across tabs
export const subscribeToPresenceUpdates = (callback) => {
  if (typeof window === 'undefined' || typeof callback !== 'function') return () => { };
  const handler = () => callback(getStoredLiveSessions());
  window.addEventListener('egurukul:presence_updated', handler);
  return () => window.removeEventListener('egurukul:presence_updated', handler);
};

// Save a session into the shared registry
export const recordUserSession = (user, currentScreen = 'Active in Workspace') => {
  if (!user) return;
  try {
    const email = (user.email || '').toLowerCase().trim();
    const id = user.userId || user.id || email;
    if (!id && !email) return;

    const name = user.name ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.username ||
      (email ? email.split('@')[0] : 'User');

    const role = (user.userType || user.role || 'ADMIN').toUpperCase();
    const now = Date.now();

    const isMac = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac');
    const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad/i.test(navigator.userAgent);
    const deviceLabel = isMobile
      ? 'Mobile (EducateMobile App)'
      : (isMac ? 'Web (Chrome • macOS)' : 'Web (Desktop Browser)');

    // A user session belongs to an organization ONLY if the user object explicitly has an orgId.
    // NEVER fall back to localStorage orgId, as that would misattribute new signup users or cross-org logins.
    const userOrg = user.orgId || user.orgDetails?.orgId || '';
    if (!userOrg) {
      // User has no confirmed organization association; do not record in multi-tenant presence pool
      return;
    }

    const sessions = getStoredLiveSessions();
    const sessionData = {
      id,
      email,
      name,
      role,
      lastActive: now,
      currentScreen: currentScreen || 'Active in Workspace',
      device: deviceLabel,
      deviceType: isMobile ? 'mobile' : 'desktop',
      orgId: userOrg
    };

    const primaryKey = (email || id).toLowerCase();
    sessions[primaryKey] = sessionData;

    safeSetItem(STORAGE_KEY, JSON.stringify(sessions));

    // Broadcast update to other tabs/windows
    if (broadcastChannel) {
      broadcastChannel.postMessage({ type: 'HEARTBEAT', session: sessionData });
    }
  } catch (err) {
    console.warn('[Presence] Failed to record user session:', err);
  }
};

// Check if a user is currently active or idle
export const getUserLiveStatus = (emailOrId, storedSessions) => {
  if (!emailOrId) return 'OFFLINE';
  const key = String(emailOrId).toLowerCase().trim();
  const sessions = storedSessions || getStoredLiveSessions();
  const session = sessions[key] || Object.values(sessions).find(s =>
    (s.id && String(s.id).toLowerCase() === key) || (s.email && String(s.email).toLowerCase() === key)
  );

  if (!session || !session.lastActive) return 'OFFLINE';

  const diff = Date.now() - session.lastActive;
  if (diff <= ACTIVE_THRESHOLD_MS) {
    return 'ACTIVE_NOW';
  }
  if (diff <= IDLE_THRESHOLD_MS) {
    return 'IDLE';
  }
  return 'OFFLINE';
};

// Setup heartbeat in active tab
export const startPresenceHeartbeat = (currentUser) => {
  if (!currentUser) return () => { };

  recordUserSession(currentUser, window.location.pathname);

  const intervalId = setInterval(() => {
    recordUserSession(currentUser, window.location.pathname);
  }, 20000); // Heartbeat every 20s

  return () => clearInterval(intervalId);
};
